import { History, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader } from "../ui/card";
import type { Payment } from "../../types/membership";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";

interface PaymentHistoryProps {
  payments: Payment[];
  variant?: "default" | "compact";
}

const toDisplayLabel = (value: string) =>
  value
    .toLowerCase()
    .split(/([\s_-]+)/)
    .map((part) => (/[a-záéíóúñ]/i.test(part) ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join("");

export function PaymentHistorySection({ payments, variant = "default" }: PaymentHistoryProps) {
  const { language } = useAuth();

  if (variant === "compact") {
    return (
      <div className="border-y border-gray-100 py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white text-gray-700 ring-1 ring-gray-200">
              <History className="h-4 w-4" />
            </div>
            <h2 className="text-base font-semibold text-gray-950">
              {dictionary["payment-history"][language]}
            </h2>
          </div>
          <span className="text-xs font-medium text-gray-400">
            {payments.length}
          </span>
        </div>

        <div className="border-t border-gray-100">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="flex items-center justify-between gap-4 border-b border-gray-100 py-3 last:border-b-0"
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-950">
                  ${payment.total_value}
                </p>
                <p className="mt-0.5 truncate text-xs text-gray-500">
                  {toDisplayLabel(payment.provider_id)} - {toDisplayLabel(payment.origin)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-xs font-medium text-gray-700">
                  {new Date(payment.created_at).toLocaleDateString()}
                </p>
                <span className="mt-1 inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                  {dictionary["succeeded"][language] || payment.state}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <Card>
      <CardHeader className="p-4 md:p-6">
        <div className="flex items-center space-x-2 md:space-x-3">
          <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
            <History className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
          </div>
          <h3 className="text-lg md:text-xl font-bold text-gray-900">
            {dictionary["payment-history"][language]}
          </h3>
        </div>
      </CardHeader>
      <CardContent className="p-4 md:p-6 pt-0">
        <div className="space-y-3 md:space-y-4">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 md:p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 gap-3"
            >
              <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm md:text-base text-gray-900">
                    ${payment.total_value}
                  </p>
                  <p className="text-xs md:text-sm text-gray-500 truncate">
                    {payment.provider_id} - {payment.origin}
                  </p>
                </div>
              </div>
              <div className="flex sm:flex-col sm:text-right items-center sm:items-end justify-between sm:justify-start gap-2 flex-shrink-0">
                <p className="text-xs md:text-sm font-medium text-gray-900">
                  {new Date(payment.created_at).toLocaleDateString()}
                </p>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap`}>
                  {dictionary["succeeded"][language] || payment.state}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
